import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Film, Loader2, Search, PlayCircle } from "lucide-react";
import { endpoints } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { HighlightCard, HighlightPlayer } from "@/components/football/Highlights";
import { CountryBar, CompBar, ResultRow, AddVideoDialog } from "@/components/football/HlResults";

const dayLabel = (d) => {
  const t = new Date(); const iso = (x) => x.toISOString().slice(0, 10);
  if (d === iso(t)) return "Today";
  t.setDate(t.getDate() - 1);
  if (d === iso(t)) return "Yesterday";
  return new Date(`${d}T12:00:00Z`).toLocaleDateString([], { weekday: "long", day: "numeric", month: "short" });
};

const SearchResults = ({ q, items, onPlay }) => {
  const s = q.trim().toLowerCase();
  const hits = (items || []).filter((h) => `${h.title} ${h.league || ""}`.toLowerCase().includes(s)).slice(0, 24);
  return hits.length === 0 ? <p className="text-sm text-slate-500 py-8 text-center" data-testid="hl-search-empty">No videos match “{q}”.</p> : (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="hl-search-results">
      {hits.map((h) => <HighlightCard key={`${h.kind}-${h.id}`} h={h} onPlay={onPlay} wide />)}
    </div>
  );
};

export default function HighlightsPage() {
  const { isAdminArea } = useAuth();
  const [sp, setSp] = useSearchParams();
  const nav = useNavigate();
  const [countries, setCountries] = useState([]);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [play, setPlay] = useState(null);
  const [add, setAdd] = useState(null);
  const [q, setQ] = useState("");
  const [all, setAll] = useState(null);
  const country = countries.find((c) => c.name === sp.get("c")) || countries[0];
  const league = Number(sp.get("l")) || country?.competitions[0]?.id;

  useEffect(() => { endpoints.hlCatalog().then((r) => setCountries(r.data.countries || [])).catch(() => setErr("Could not load competitions.")); }, []);
  const load = () => {
    if (!league) return;
    setData(null); setErr("");
    endpoints.hlResults(league).then((r) => setData(r.data)).catch((e) => setErr(e?.response?.data?.detail || "Results are not available right now."));
  };
  useEffect(load, [league]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (q && all === null) endpoints.highlights(120).then((r) => setAll(r.data.highlights || [])).catch(() => setAll([])); }, [q, all]);

  const pick = (c, l) => setSp((p) => { const n = new URLSearchParams(p); n.set("c", c.name); n.set("l", String(l || c.competitions[0]?.id)); return n; }, { replace: true });
  const days = useMemo(() => {
    const g = {};
    (data?.matches || []).forEach((m) => { (g[(m.date || "").slice(0, 10)] ||= []).push(m); });
    return Object.entries(g);
  }, [data]);
  const withVideo = (data?.matches || []).filter((m) => m.video).length;
  const watch = (m) => setPlay({ ...m.video, kind: "clip", homeName: m.home.name, awayName: m.away.name, league: data?.competition?.name, matchDate: (m.date || "").slice(0, 10) });
  const openClip = (h) => (h.kind === "clip" ? setPlay(h) : nav(h.link));

  return (
    <div className="px-3 sm:px-6 lg:px-8 max-w-5xl mx-auto py-6 sm:py-10 pb-28" data-testid="highlights-page">
      <div className="flex flex-col sm:flex-row sm:items-end gap-3 mb-4">
        <div className="flex-1">
          <div className="text-xs uppercase tracking-widest text-[#E11D2A] font-bold flex items-center gap-1.5"><Film className="w-4 h-4" /> Football</div>
          <h1 className="text-3xl sm:text-5xl font-extrabold text-white mt-1">Highlights</h1>
        </div>
        <label className="h-10 px-3 rounded-full bg-[#121620] border border-[#273042] flex items-center gap-2 w-full sm:w-72">
          <Search className="w-4 h-4 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search any team or league" data-testid="highlights-search" className="w-full bg-transparent text-sm text-white outline-none placeholder-slate-500" />
        </label>
      </div>

      {q.trim() ? (all === null ? <Loader2 className="w-6 h-6 animate-spin text-[#E11D2A]" /> : <SearchResults q={q} items={all} onPlay={openClip} />) : (
        <>
          <CountryBar countries={countries} value={country?.name} onPick={(c) => pick(c)} />
          {country && <div className="mt-1"><CompBar comps={country.competitions} value={league} onPick={(c) => pick(country, c.id)} /></div>}
          <div className="flex items-center gap-2 mt-3 mb-3">
            {data?.competition && <img src={data.competition.logo} alt="" className="w-6 h-6 object-contain" />}
            <h2 className="text-white font-bold text-base sm:text-lg flex-1 truncate" data-testid="hl-comp-title">{data?.competition?.name || country?.competitions[0]?.name || ""}</h2>
            {data && <span className="text-xs text-slate-400 inline-flex items-center gap-1" data-testid="hl-video-count"><PlayCircle className="w-3.5 h-3.5 text-[#E11D2A]" />{withVideo} video{withVideo === 1 ? "" : "s"} · last {data.days} days</span>}
          </div>
          {err ? <div className="rounded-2xl border border-[#273042] bg-[#121620] p-8 text-center text-slate-400 text-sm" data-testid="hl-error">{err}</div>
            : !data ? <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#E11D2A]" /></div>
            : days.length === 0 ? <div className="rounded-2xl border border-[#273042] bg-[#121620] p-8 text-center text-slate-400 text-sm" data-testid="hl-empty">No finished matches in the last {data.days} days.</div>
            : days.map(([d, ms]) => (
              <section key={d} className="mb-4" data-testid={`hl-day-${d}`}>
                <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-1.5 px-1">{dayLabel(d)}</div>
                <div className="rounded-2xl bg-[#121620] border border-[#273042] divide-y divide-[#273042] overflow-hidden">
                  {ms.map((m) => <ResultRow key={m.matchId} m={m} onWatch={watch} onAdd={isAdminArea ? setAdd : null} />)}
                </div>
              </section>))}
        </>
      )}
      {play && <HighlightPlayer h={play} onClose={() => setPlay(null)} />}
      {add && <AddVideoDialog m={add} competition={data?.competition?.name} onClose={() => setAdd(null)} onSaved={() => { setAdd(null); load(); }} />}
    </div>
  );
}
